import { ReceiptTextIcon, Users } from 'lucide-react';

import { useTranslation } from '@splitfiesta/i18n';
import {
  type LineItemSplitResult,
  SplitMethod,
  formatAmountForCurrency,
} from '@splitfiesta/shared';
import { Button, Card } from '@splitfiesta/ui';

import { ScanNotReconciledWarning } from '@/components/expenses/lineItems/ScanNotReconciledWarning';

import { resolveUserName } from '@/lib/displayName';

// Using the Split interface from useExpenseForm
interface Split {
  userId: string;
  userName?: string;
  profileImage?: string;
  amount?: number;
  amountFormatted?: string;
  percentage?: number;
  shares?: number;
  included: boolean;
}

interface SplitRecapProps {
  splits: Split[];
  totalAmount: number;
  currency: string;
  splitMethod: SplitMethod;
  paidBy: Array<{ userId: string; amount: number }>;
  currentUserId: string;
  onExpand?: () => void;
  isLoading?: boolean;
  isExpanded?: boolean;
  /** Set while splitting by item; the stored `custom` method is not what the person chose. */
  items?: {
    /** Items mode is on; otherwise a scanned list waits for "Split by item". */
    active: boolean;
    count: number;
    fromScan: boolean;
    /** A scan that did not add up, with its list not edited since. */
    unreconciled: boolean;
    shares: LineItemSplitResult['entryShares'];
    /** The entry currency the shares are in. */
    currency: string;
  };
  onSplitByItem?: () => void;
}

export function SplitRecap({
  splits,
  totalAmount,
  currency,
  splitMethod,
  paidBy,
  currentUserId,
  onExpand,
  isLoading = false,
  isExpanded = false,
  items,
  onSplitByItem,
}: SplitRecapProps) {
  const { t } = useTranslation('expenses');

  // Calculate included members
  const includedSplits = splits.filter(s => s.included);
  const includedCount = includedSplits.length;

  // Get payer info
  const currentUserPaid = paidBy.find(p => p.userId === currentUserId);
  const isMultiplePayers = paidBy.length > 1;
  const totalPaid = paidBy.reduce((sum, p) => sum + p.amount, 0);

  // Calculate individual amounts for equal split
  const amountPerPerson = includedCount > 0 ? totalAmount / includedCount : 0;

  // Get display names for first few members. Only real names get the
  // first-word treatment — localized labels ("Deleted user", "Unknown")
  // must stay whole or they read as fragments ("Deleted", "Usuario").
  const displayMembers = includedSplits.slice(0, 3).map(s => {
    const resolved = resolveUserName(s.userName, t);
    const isRealName = Boolean(s.userName) && resolved === s.userName;
    return isRealName ? resolved.split(' ')[0] : resolved;
  });
  const remainingCount = includedCount - 3;

  // Determine split method label
  const getSplitMethodLabel = () => {
    switch (splitMethod) {
      case 'equal':
        return t('splitMethods.collapsedEqual');
      case 'percentage':
        return t('splitMethods.collapsedPercentage');
      case 'shares':
        return t('splitMethods.collapsedShares');
      case 'custom':
        return t('splitMethods.collapsedCustom');
      default:
        return t('splitMethods.equal');
    }
  };

  const handleCardClick = () => {
    if (onExpand) {
      onExpand();
    }
  };

  return (
    <div
      className={`
        cursor-pointer hover:shadow-md transition-all duration-200
        ${isLoading ? 'opacity-50 pointer-events-none' : ''}
      `}
      onClick={handleCardClick}
    >
      <Card className="bg-linear-to-r from-emerald-50/50 to-teal-50/50 dark:from-emerald-900/10 dark:to-teal-900/10 border-emerald-200 dark:border-emerald-800">
        <div className="space-y-2">
          {/* Payer Information */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              {isMultiplePayers ? (
                <>
                  <strong className="text-gray-900 dark:text-gray-100">
                    {t('labels.multiplePeoplePaid')}
                  </strong>
                  {currentUserPaid && (
                    <>
                      {' '}
                      ({t('labels.includingYou')}:{' '}
                      <strong className="text-gray-900 dark:text-gray-100">
                        {formatAmountForCurrency(
                          currentUserPaid.amount,
                          currency
                        )}
                      </strong>
                      )
                    </>
                  )}
                </>
              ) : currentUserPaid ? (
                <>
                  {t('labels.youPaidAmount')}{' '}
                  <strong className="text-gray-900 dark:text-gray-100">
                    {formatAmountForCurrency(currentUserPaid.amount, currency)}
                  </strong>
                </>
              ) : (
                <>
                  {paidBy[0] &&
                    resolveUserName(
                      splits.find(s => s.userId === paidBy[0].userId)?.userName,
                      t
                    )}{' '}
                  {t('labels.someonePaid')}{' '}
                  <strong className="text-gray-900 dark:text-gray-100">
                    {formatAmountForCurrency(totalPaid, currency)}
                  </strong>
                </>
              )}
            </span>
          </div>

          {/* Split Method and Participants */}
          {items?.active ? (
            <div className="flex flex-wrap items-center gap-2">
              <ReceiptTextIcon
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500"
              />
              <span className="min-w-[45%] flex-1 text-sm text-gray-600 dark:text-gray-400">
                {t('lineItems.collapsed', { count: includedCount })}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {getSplitMethodLabel()} • {includedCount}{' '}
                {includedCount === 1 ? t('labels.person') : t('labels.people')}
              </span>
            </div>
          )}

          {items ? (
            <div className="space-y-2">
              {items.fromScan && !items.active ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('lineItems.readCount', { count: items.count })}
                </p>
              ) : null}
              {items.unreconciled ? <ScanNotReconciledWarning /> : null}
              {items.active && items.shares.length > 0 ? (
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
                  {items.shares.map(share => (
                    <span key={share.userId}>
                      <span className="text-gray-500 dark:text-gray-400">
                        {resolveUserName(
                          splits.find(s => s.userId === share.userId)?.userName,
                          t
                        )}
                      </span>{' '}
                      <strong className="text-gray-900 dark:text-gray-100">
                        {formatAmountForCurrency(share.amount, items.currency)}
                      </strong>
                    </span>
                  ))}
                </div>
              ) : null}
              {onSplitByItem ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={event => {
                    event.stopPropagation();
                    onSplitByItem();
                  }}
                  disabled={isLoading}
                  className="min-h-11"
                >
                  {t('lineItems.splitByItem')}
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* Amount per person (only shown for equal split) */}
          {totalAmount > 0 &&
            includedCount > 0 &&
            splitMethod === 'equal' &&
            !items?.active && (
              <div className="flex items-center justify-between pt-2 border-t border-emerald-200/50 dark:border-emerald-800/50">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t('labels.eachPersonOwes')}
                </span>
                <span className="text-base font-semibold text-emerald-600 dark:text-emerald-400">
                  {formatAmountForCurrency(amountPerPerson, currency)}
                </span>
              </div>
            )}

          {/* Member preview */}
          {includedCount > 0 && !items?.active && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {displayMembers.join(', ')}
              {remainingCount > 0 && ` +${remainingCount} ${t('labels.more')}`}
            </div>
          )}

          {/* Click to customize hint */}
          <div className="text-xs text-primary-500 dark:text-primary-400 text-center font-medium">
            {isExpanded
              ? t('labels.clickToCollapse')
              : t('labels.clickToCustomizeSplit')}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default SplitRecap;
